import CardsIcon from '@assets/icons/card.svg?react'
import CryptoIcon from '@assets/icons/crypto.svg?react'
import ExpensesIcon from '@assets/icons/expenses.svg?react'
import HeadsetIcon from '@assets/icons/headset.svg?react'
import HomeIcon from '@assets/icons/home.svg?react'
import InviteAndEarnIcon from '@assets/icons/invite-and-earn.svg?react'
import InvoicesIcon from '@assets/icons/invoices.svg?react'
import KingdomCashIcon from '@assets/icons/kingodm-cash.svg?react'
import OverdraftIcon from '@assets/icons/overdraft.svg?react'
import RequestIbanIcon from '@assets/icons/request-iban.svg?react'
import SavingsIcon from '@assets/icons/savings.svg?react'
import TransactionsIcon from '@assets/icons/transactions.svg?react'
import UserIcon from '@assets/icons/user.svg?react'
import WalletIcon from '@assets/icons/wallet.svg?react'
import {
  isOwnerOrAdministrator,
  paths,
  useMerchantAccounts,
  useMerchantQry,
  useMyAccountAccessQry,
  useUserProfileQry
} from 'mmfintech-backend-api'
import { isValidArray } from 'mmfintech-commons'
import { type ComponentType, useMemo } from 'react'

export interface MenuItem {
  // imageSrc: string
  icon: ComponentType
  label: string
  path?: string
  localization?: string
  onClick?: () => void
  toolTip?: string
}

export const useMenuItems = (): MenuItem[] => {
  const { merchant } = useMerchantQry()
  const { profile } = useUserProfileQry()
  const { paymentAccounts } = useMerchantAccounts()

  const accountAccess = useMyAccountAccessQry()

  const hasAccounts = useMemo(() => isValidArray(paymentAccounts), [paymentAccounts])

  return useMemo(() => {
    const isApprover = isValidArray(profile?.userPermissions) && profile?.userPermissions.includes('APPROVER')
    const isSubmitter = isValidArray(profile?.userPermissions) && profile?.userPermissions.includes('SUBMITTER')

    const isStaffWithNoAccounts = !isOwnerOrAdministrator(profile?.customerRole) && !hasAccounts

    const res: MenuItem[] = []

    if (!isStaffWithNoAccounts) {
      res.push({
        icon: HomeIcon,
        label: 'Overview',
        path: paths.dashboard(),
        localization: 'FRONTEND.NAVIGATION.DASHBOARD',
        toolTip: 'Overview'
      })
    }

    if (hasAccounts) {
      res.push({
        icon: WalletIcon,
        label: 'Accounts',
        path: paths.banking.accounts.list(),
        localization: 'FRONTEND.NAVIGATION.ACCOUNTS',
        toolTip: 'Accounts'
      })
    }

    if (hasAccounts && (merchant?.capabilities?.enableCardIssuing || profile?.isCardIssuingEnabled)) {
      res.push({
        icon: CardsIcon,
        label: 'Cards',
        path: paths.cards.list(),
        localization: 'FRONTEND.NAVIGATION.CARDS',
        toolTip: 'Cards'
      })
    }

    if (accountAccess?.hasAnyPermissionToCryptoAccounts) {
      res.push({
        icon: CryptoIcon,
        label: 'Crypto',
        path: paths.crypto.root(),
        localization: 'FRONTEND.NAVIGATION.CRYPTO',
        toolTip: 'Crypto'
      })
    }

    if (hasAccounts) {
      res.push({
        icon: TransactionsIcon,
        label: 'Transactions',
        path: paths.banking.transactions.list(),
        localization: 'FRONTEND.NAVIGATION.TRANSACTIONS',
        toolTip: 'Transactions'
      })
    }

    res.push({
      icon: UserIcon,
      label: 'Recipients',
      path: paths.recipients.list(),
      localization: 'FRONTEND.NAVIGATION.RECIPIENTS',
      toolTip: 'Recipients'
    })

    if (hasAccounts) {
      res.push({
        icon: InvoicesIcon,
        label: 'Invoices',
        path: paths.invoicesCustomersWrapper(),
        localization: 'FRONTEND.NAVIGATION.INVOICES',
        toolTip: 'Invoices'
      })
    }

    if (isOwnerOrAdministrator(profile?.customerRole)) {
      res.push({
        icon: SavingsIcon,
        label: 'Savings',
        path: paths.more.savings(),
        localization: 'FRONTEND.NAVIGATION.SAVINGS',
        toolTip: 'Savings'
      })

      if (merchant?.accountType === 'COMPANY' && merchant?.capabilities?.enableOverdraftCreation) {
        res.push({
          icon: OverdraftIcon,
          label: 'Overdraft',
          path: paths.overdraft(),
          localization: 'FRONTEND.NAVIGATION.OVERDRAFT',
          toolTip: 'Overdraft'
        })
      }

      if (merchant?.capabilities?.enableCashVouchers) {
        res.push({
          icon: KingdomCashIcon,
          label: 'Kingdom Cash',
          path: paths.more.cashVoucher('buy'),
          localization: 'FRONTEND.NAVIGATION.KINGDOM_CASH',
          toolTip: 'Kingdom Cash'
        })
      }

      if (merchant?.capabilities?.allowReferrals) {
        res.push({
          icon: InviteAndEarnIcon,
          label: 'Invite & Earn',
          path: paths.referralProgram(),
          localization: 'FRONTEND.NAVIGATION.INVITE_AND_EARN',
          toolTip: 'Invite & Earn'
        })
      }
    }

    if (
      merchant?.accountType === 'COMPANY' &&
      (isOwnerOrAdministrator(profile?.customerRole) || isApprover || isSubmitter)
    ) {
      res.push({
        icon: ExpensesIcon,
        label: 'Expenses',
        path: paths.expenseManagement.dashboard(),
        localization: 'FRONTEND.NAVIGATION.EXPENSES',
        toolTip: 'Expenses'
      })
    }

    if (isOwnerOrAdministrator(profile?.customerRole) && merchant?.capabilities.ibanEligible) {
      res.push({
        icon: RequestIbanIcon,
        label: 'Request IBAN',
        path: paths.ibanIssuing(),
        localization: 'FRONTEND.NAVIGATION.REQUEST_IBAN',
        toolTip: 'Request IBAN'
      })
    }

    res.push({
      icon: HeadsetIcon,
      label: 'Customer Support',
      path: paths.support.tickets(),
      localization: 'FRONTEND.NAVIGATION.CUSTOMER_SUPPORT',
      toolTip: 'Customer Support'
    })

    return res
  }, [merchant, profile, accountAccess, hasAccounts])
}
